import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { removeTaskEntity } from './useTaskEntityCache';
import type { BulkDeleteTasksResponse } from '../types';

/**
 * Deletes multiple tasks via the bulk-delete endpoint. The backend evaluates
 * each task independently (must be archived, must have no subtasks, caller
 * must be the creator or a project Owner/Admin) and reports a per-task result
 * — rows that fail stay in the cache, rows that succeed are evicted from the
 * entity cache and all active collections.
 *
 * `projectId` is accepted for interface symmetry with other task hooks even
 * though it is unused inside — matches the pattern in useTaskFieldUpdate.
 */
export function useTaskBulkDelete(_projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';

  return useMutation({
    mutationFn: (taskIds: string[]): Promise<BulkDeleteTasksResponse> =>
      tasksService.bulkDeleteTasks({ taskIds }),
    onSuccess: (data) => {
      for (const result of data.results) {
        if (result.success) removeTaskEntity(queryClient, orgId, result.taskId);
      }
    },
  });
}
